import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getLinkedInAvatarUrl } from "@/api/linkedinService";

/**
 * ============================================================
 * WORKSHOP CARD COMPONENT
 * ============================================================
 * Clean, high-aesthetic card representing a single workshop item.
 * Designed using dark-first glassmorphism and cyan brand highlights.
 */
export default function WorkshopCard({ workshop }) {
  if (!workshop) return null;

  // Determine registration status badge text and style
  const getStatusBadge = () => {
    if (workshop.status === "Completed") {
      return {
        label: "Completed Session",
        className: "bg-white/10 text-white/70 border-white/15",
        dotClassName: "bg-white/40",
      };
    }
    if (workshop.spots_remaining > 0 && workshop.spots_remaining <= 10) {
      return {
        label: `Filling Fast (${workshop.spots_remaining} left)`,
        className: "bg-amber-500/15 text-amber-300 border-amber-500/30",
        dotClassName: "bg-amber-400 animate-pulse",
      };
    }
    if (workshop.is_registration_open) {
      return {
        label: "Registration Open",
        className: "bg-green-500/15 text-green-400 border-green-500/30",
        dotClassName: "bg-green-400",
      };
    }
    return {
      label: "Registration Closed",
      className: "bg-white/10 text-white/60 border-white/10",
      dotClassName: "bg-white/40",
    };
  };

  const statusInfo = getStatusBadge();

  return (
    <div className="group relative h-full flex flex-col">
      {/* Ambient hover glow */}
      <div className="absolute -inset-0.5 rounded-[2rem] bg-gradient-to-r from-primary/20 via-primary/10 to-transparent opacity-0 blur-lg transition-opacity duration-500 group-hover:opacity-100 -z-10" />

      {/* Card Body */}
      <div className="flex-1 rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden flex flex-col justify-between transition-all duration-300 hover:border-primary/40 hover:bg-white/[0.06] shadow-[0_18px_50px_rgba(0,0,0,0.4)]">
        <div>
          {/* Cover Image Container */}
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-black/50">
            <img
              src={workshop.cover_image}
              alt={workshop.title}
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=80";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

            {/* Top Badges */}
            <div className="absolute top-3.5 left-3.5 right-3.5 flex items-center justify-between gap-2">
              <span className="px-2.5 py-1 rounded-full bg-black/70 border border-white/20 text-white text-[11px] font-medium backdrop-blur-md">
                {workshop.category}
              </span>

              <span className="px-2.5 py-1 rounded-full bg-primary/20 text-primary border border-primary/30 text-[11px] font-semibold backdrop-blur-md">
                {workshop.mode}
              </span>
            </div>

            {/* Bottom Badges */}
            <div className="absolute bottom-3 left-3.5 right-3.5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-white/15 bg-black/60 backdrop-blur-md text-[11px]">
                <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dotClassName}`} />
                <span className="text-white/80">{statusInfo.label}</span>
              </div>

              {workshop.is_free ? (
                <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-green-500/20 border border-green-500/30 text-[11px] font-bold backdrop-blur-md">
                  <span className="text-[10px] text-white/50 line-through font-normal">₹999</span>
                  <span className="text-green-400">FREE</span>
                </div>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[11px] font-bold backdrop-blur-md">
                  ₹{workshop.price?.toLocaleString("en-IN")}
                </span>
              )}
            </div>
          </div>

          {/* Text Content */}
          <div className="p-6 space-y-3">
            {/* Meta Row */}
            <div className="flex items-center gap-3 text-xs text-white/50">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-primary" />
                <span>{workshop.date}</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-primary" />
                <span>{workshop.duration}</span>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-lg font-bold tracking-tight text-white group-hover:text-primary transition-colors leading-snug line-clamp-2">
              {workshop.title}
            </h3>

            {/* Short Description */}
            <p className="text-xs text-white/60 leading-relaxed line-clamp-2">
              {workshop.short_description}
            </p>
          </div>
        </div>

        {/* Card Footer: Instructor & CTA */}
        <div className="p-6 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
          {/* Instructor snippet */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg overflow-hidden border border-white/10 bg-white/5 flex-shrink-0">
              {workshop.instructor?.avatar_url || workshop.instructor?.linkedin_url ? (
                <img
                  src={workshop.instructor?.avatar_url || getLinkedInAvatarUrl(workshop.instructor?.linkedin_url)}
                  alt={workshop.instructor.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    const fallback = e.target.nextSibling;
                    if (fallback) fallback.style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className={`w-full h-full items-center justify-center text-primary ${
                  workshop.instructor?.avatar_url || workshop.instructor?.linkedin_url ? "hidden" : "flex"
                }`}
              >
                <User className="w-4 h-4" />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-white truncate">
                {workshop.instructor?.name}
              </div>
              <div className="text-[10px] text-white/45 truncate">
                {workshop.instructor?.role}
              </div>
            </div>
          </div>

          {/* Action Link */}
          <Link to={`/workshops/${workshop.slug}`} className="flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-9 px-3.5 rounded-xl border-white/15 bg-white/[0.04] text-xs font-semibold text-white",
                "group-hover:border-primary/50 group-hover:bg-primary group-hover:text-black transition-all duration-200 gap-1.5"
              )}
            >
              <span>Details</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
